import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { Card } from "shared/components/Card";
import { DatePicker } from "shared/components/DatePickerCalendar";
import { getErrorMessage } from "utils/error";

export const ChangeDesiredMoveInDateModal = ({
  opened,
  onClose,
  applicationId,
  desiredMoveInDate,
  refetchApplication
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
  desiredMoveInDate: string;
  refetchApplication: () => void;
}) => {
  const [selectedDesiredMoveInDate, setSelectedDesiredMoveInDate] =
    useState(desiredMoveInDate);

  const { mutate: updateDesiredMoveInDate, isPending } = useMutation({
    mutationKey: ["UPDATE_DESIRED_MOVE_IN_DATE"],
    mutationFn: (desiredMoveInDate: string) => {
      return instance.patch(`/portal/v1/applications/${applicationId}`, {
        desired_move_in_date: desiredMoveInDate
      });
    },
    onSuccess: () => {
      refetchApplication();
      onClose();
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response?.status === 403) {
        toast.error(
          "You do not have permission to change desired move-in date"
        );
        return;
      }
      toast.error(getErrorMessage(error) || "Error");
    }
  });

  useEffect(() => {
    setSelectedDesiredMoveInDate(desiredMoveInDate);
  }, [desiredMoveInDate]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[452px] w-full rounded-xl"
    >
      <Card className="bg-background-1000">
        <div className="flex items-center justify-between h-[64px] px-4">
          <h3 className="text-base font-bold text-content-1000">
            Desired move-in date
          </h3>
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.BASE}
            onClick={onClose}
            HugeIcon={Cancel01Icon}
          />
        </div>
        <div>
          <div className="border-y border-alpha-80">
            <DatePicker
              selectedDate={selectedDesiredMoveInDate}
              onSelectDate={(date) => {
                setSelectedDesiredMoveInDate(date);
              }}
            />
          </div>
          <div className="flex justify-end gap-4 p-3 bg-white">
            <Button size={ButtonSize.SM} onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              loading={isPending}
              className="flex-1"
              onClick={() => {
                updateDesiredMoveInDate(selectedDesiredMoveInDate);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
  );
};
